// src/components/admin/categories/SubcategoryDialog.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from '@/components/ui/switch'; // Restaurado Switch para Status
import { toast } from '@/components/ui/use-toast';
import { getCategories, createSubcategory, updateSubcategory } from '@/lib/categories';
import type { Category, Subcategory, SubcategoryFormData } from '@/types/category';

interface SubcategoryDialogProps {
    isOpen: boolean;
    mode: 'addSubcategory' | 'editSubcategory' | null;
    itemToEdit?: Subcategory | null;
    onClose: () => void;
    onSuccess: () => void;
}

// Dados padrão incluem status agora
const defaultSubcategoryData: Omit<SubcategoryFormData, 'categoryId'> = {
    name: '',
    description: '',
    status: 'active',
    displayOrder: 0,
};

export function SubcategoryDialog({ isOpen, mode, itemToEdit, onClose, onSuccess }: SubcategoryDialogProps) {
    const [formData, setFormData] = useState<SubcategoryFormData | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Busca categorias para o dropdown
    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            getCategories()
                .then(data => {
                    setCategories(data);
                    setError(null);
                })
                .catch(err => {
                    console.error("Erro ao buscar categorias para o diálogo:", err);
                    setError("Falha ao carregar categorias pai.");
                    toast({ title: 'Erro', description: 'Não foi possível carregar as categorias pai.', variant: 'destructive' });
                    setCategories([]);
                })
                .finally(() => setIsLoading(false));
        }
    }, [isOpen]);

    // Inicializa formulário
    useEffect(() => {
        setError(null);
        if (mode === 'editSubcategory' && itemToEdit) {
            setFormData({
                name: itemToEdit.name,
                description: itemToEdit.description || '',
                status: itemToEdit.status, // Status carregado
                displayOrder: itemToEdit.displayOrder,
                categoryId: itemToEdit.categoryId,
            });
        } else if (mode === 'addSubcategory') {
             setFormData({ ...defaultSubcategoryData, categoryId: '' });
        } else {
            setFormData(null);
        }
    }, [isOpen, mode, itemToEdit]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => prev ? { ...prev, [name]: value } : null);
    };

    // Handler para Switch de status
    const handleSwitchChange = (checked: boolean) => {
        setFormData(prev => prev ? { ...prev, status: checked ? 'active' : 'inactive' } : null);
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => prev ? { ...prev, [name]: parseInt(value, 10) || 0 } : null);
    }

     const handleCategoryChange = (value: string) => {
         setFormData(prev => prev ? { ...prev, categoryId: value } : null);
     };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!formData || isLoading || !formData.categoryId) {
            setError("Por favor, selecione uma categoria pai.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            let result;
            const dataToSend = { ...formData }; // Inclui status

            if (mode === 'addSubcategory') {
                result = await createSubcategory(dataToSend as SubcategoryFormData);
            } else if (mode === 'editSubcategory' && itemToEdit) {
                result = await updateSubcategory(itemToEdit.id, dataToSend as Partial<SubcategoryFormData>);
            } else {
                 throw new Error("Modo inválido ou item ausente para submissão.");
            }

            if (result && result.error) {
                 throw new Error(result.error);
             }

            toast({ title: 'Sucesso!', description: `Subcategoria ${mode?.includes('add') ? 'criada' : 'atualizada'} com sucesso.` });
            onSuccess();

        } catch (err: any) {
            console.error("Falha na operação da subcategoria:", err);
            const errorMessage = err.message || 'Ocorreu um erro desconhecido.';
            setError(errorMessage);
            toast({ title: 'Erro', description: errorMessage, variant: 'destructive' });
        } finally {
            setIsLoading(false);
        }
    };

    let title = '';
    if (mode === 'addSubcategory') title = 'Adicionar Nova Subcategoria';
    if (mode === 'editSubcategory') title = 'Editar Subcategoria';

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {/* <DialogDescription>...</DialogDescription> */}
                </DialogHeader>
                {formData ? (
                    <form onSubmit={handleSubmit} className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="categoryId" className="text-right">
                                Categoria Pai
                            </Label>
                            <Select
                                value={formData.categoryId}
                                onValueChange={handleCategoryChange}
                                required
                                disabled={isLoading || categories.length === 0} // Desabilita se carregando ou sem categorias
                            >
                                <SelectTrigger className="col-span-3">
                                    <SelectValue placeholder="Selecione a categoria principal..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.length === 0 && !isLoading && <SelectItem value="" disabled>Nenhuma categoria encontrada</SelectItem>}
                                    {isLoading && <SelectItem value="" disabled>Carregando...</SelectItem>}
                                    {categories.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                         </div>
                         <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                                Nome
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="col-span-3"
                                required
                                disabled={isLoading}
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="description" className="text-right">
                                Descrição
                            </Label>
                            <Textarea
                                id="description"
                                name="description"
                                value={formData.description || ''}
                                onChange={handleInputChange}
                                className="col-span-3"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="displayOrder" className="text-right">
                                Ordem
                            </Label>
                            <Input
                                id="displayOrder"
                                name="displayOrder"
                                type="number"
                                min="0"
                                value={formData.displayOrder}
                                onChange={handleNumberChange}
                                className="col-span-3"
                                required
                                disabled={isLoading}
                            />
                        </div>
                         {/* Campo de Status restaurado */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="status" className="text-right">
                                Status
                            </Label>
                            <div className="col-span-3 flex items-center space-x-2">
                                 <Switch
                                     id="status"
                                     checked={formData.status === 'active'}
                                     onCheckedChange={handleSwitchChange}
                                     disabled={isLoading}
                                 />
                                 <span>{formData.status === 'active' ? 'Ativo' : 'Inativo'}</span>
                            </div>
                        </div>
                        {error && <p className="text-red-500 text-sm col-span-4 text-center">{error}</p>}
                        <DialogFooter>
                             <DialogClose asChild>
                                <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>Cancelar</Button>
                             </DialogClose>
                            <Button type="submit" disabled={isLoading || !formData.categoryId}>{isLoading ? 'Salvando...' : 'Salvar'}</Button>
                        </DialogFooter>
                    </form>
                 ) : (
                    <div className="py-4 text-center">Carregando formulário...</div>
                 )}
            </DialogContent>
        </Dialog>
    );
}
